import { useState, useEffect, useCallback } from "react";
import { ShoppingCart, Ticket, Monitor, LogIn, LogOut, User } from "lucide-react";
import React from "react";
import PlayerBalance from "./PlayerBalance";
import LoginDialog from "./LoginDialog";
import ReceiptDialog, { ReceiptMessage } from "./ReceiptDialog";
import './styles.css'
import { AuthResponse, getIframeUrl, TerminalMode, UserData } from "./utils";


export default function Home() {
  const defaultUser: UserData = {
    id: "0",
    token: "",
    balance: undefined,
    username: ""
  };

  const [userData, setUserData] = useState<UserData>(defaultUser);
  const [url, setUrl] = useState<string>("");
  const [mode, setMode] = useState<TerminalMode>("buy");
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [receiptData, setReceiptData] = useState<ReceiptMessage | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);

  useEffect(() => {
    setUrl(getIframeUrl(userData, mode));
  }, [mode, userData]);

  // Listen for printStoreReceipt messages from the iframe
  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      if (event.data?.type === "printStoreReceipt") {
        setReceiptData(event.data as ReceiptMessage);
        setShowReceipt(true);
        console.log("Received printStoreReceipt:", event.data);
      }
    };
    window.addEventListener("message", handlePostMessage);
    return () => window.removeEventListener("message", handlePostMessage);
  }, []);

  const checkAuthStatus = async (playerId: string, token: string) => {
    try {
      const response = await fetch(
        "https://onextwo-mockopservice-dljma65rcq-ew.a.run.app/players/" + playerId,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const userData: UserData = await response.json();
        setUserData(userData);
        return userData;
      } else {
        console.error("Request failed with status:", response.status);
      }
    } catch (error) {
      console.error("Failed to check auth status:", error);
    }
    return defaultUser;
  };

  const handleLogout = async () => {
    setUserData(defaultUser);
  };

  const handleLoginSuccess = async (response: AuthResponse) => {
    checkAuthStatus(response.playerId as string, response.token as string).then(
      (user) => { setUrl(getIframeUrl(user, mode)) }
    );
  };



  return (
    <div className="min-h-screen bg-gradient-main body">
      {/* Header */}
      <div className="header">
        <div className="header-container">
          <div className="flex items-center justify-between">
            {/* Logo and Title */}
            <div className="flex items-center space-x-3">
              <div className="logo">
                <span className="text-white font-bold text-lg">BT</span>
              </div>
              <h1 className="text-2xl font-bold text-white">BetTerminal</h1>
            </div>

            {/* User Section */}
            <div className="flex items-center space-x-4">
              {userData.username !== "" ? (
                <>
                  <PlayerBalance user={userData} />
                  <div className="user-info space-x-2">
                    <User className="icon-sm" />
                    <span className="text-sm">{userData.username}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="btn btn-logout space-x-2"
                  >
                    <LogOut className="icon-sm" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowLoginDialog(true)}
                  className="btn btn-login space-x-2"
                >
                  <LogIn className="icon-sm" />
                  <span>Login</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-container">
        {/* Control Buttons */}
        <div className="control-buttons">
          <div className="flex space-x-4">
            <button
              onClick={() => {
                setMode("buy");
              }}
              className={`btn btn-buy space-x-2 ${mode === "buy" ? "active" : ""}`}
            >
              <ShoppingCart className="icon-md" />
              <span>Buy</span>
            </button>

            <button
              onClick={() => {
                setMode("coupons");
              }}
              className={`btn btn-coupons space-x-2 ${mode === "coupons" ? "active" : ""}`}
            >
              <Ticket className="icon-md" />
              <span>Coupons</span>
            </button>

            <button
              onClick={() => {
                setMode("display");
              }}
              className={`btn btn-coupons space-x-2 ${mode === "display" ? "active" : ""}`}
            >
              <Monitor className="icon-md" />
              <span>Display</span>
            </button>
          </div>
        </div>

        {/* Terminal Interface */}
        <div className="terminal-container">
          <div className="terminal-header">
            <h2 className="text-lg font-semibold text-white">
              {mode === "buy" ? "Purchase Terminal" : mode === "coupons" ? "Coupons" : "Display"}
            </h2>
            <p className="text-slate-300 text-sm" style={{ marginTop: '0.25rem' }}>
              {mode === "buy"
                ? "Browse and purchase betting options"
                : mode === "coupons"
                ? "View available coupons"
                : "View betting terminal display"}
              {mode === "display"
                ? " — customer screen 926 × 400"
                : " — clerk screen 1072 × 500"}
            </p>
          </div>

          <div className="terminal-content">
            <div className="iframe-container">
              {userData.username !== "" ? <iframe
                src={url}
                key={url}
                className={`iframe ${mode === "display" ? "iframe-customer" : "iframe-clerk"}`}
                title={`Betting Terminal - ${mode}`}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                allow="web-share"
              /> :
                (
                  <div className="login-message-container">
                    <div className="login-message">
                      <LogIn className="login-message-icon" />
                      <h3 className="login-message-title">Authentication Required</h3>
                      <p className="login-message-text">
                        Please log in to access the betting terminal and place your bets.
                      </p>
                      <button
                        onClick={() => setShowLoginDialog(true)}
                        className="btn btn-login login-message-button space-x-2"
                      >
                        <LogIn className="icon-md" />
                        <span>Login Now</span>
                      </button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer">
          <p>Current mode: <span className="footer-highlight">{mode}</span></p>
          {userData && (
            <p style={{ marginTop: '0.25rem' }}>Logged in as: <span className="footer-highlight">{userData.username}</span></p>
          )}
        </div>
      </div>

      {/* Login Dialog */}
      <LoginDialog
        isOpen={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Receipt Dialog */}
      <ReceiptDialog
        isOpen={showReceipt}
        onClose={() => setShowReceipt(false)}
        receipt={receiptData}
      />
    </div>
  );
}
